import { useQuery } from '@apollo/client';
import { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { AppointmentStatusLabel } from '@eluve/blocks';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import {
  useAppointmentId,
  useAppointmentPatient,
  useIsUserAppointmentOwner,
} from '@eluve/frontend-appointment-hooks';
import {
  AppointmentLanguageSelector,
  NewAppointmentTemplateSelector,
  useAppointmentBundleContext,
} from '@eluve/frontend-feature-appointment';
import { ResultOf, graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { TenantOrUserFeatureFlaggedComponent } from '@eluve/smart-blocks';
import { useDialog } from '@eluve/utility-hooks';

import { DuplicateChartCard } from './DuplicateChartCard';

const duplicateAppointmentFragment = graphql(`
  fragment duplicatedAppointment on Appointments @_unmask {
    __typename
    id
    name
    startDate
    status
    llm_output_template {
      __typename
      id
      name
    }
    doctor_interaction {
      __typename
      appointmentId
      noteSignedAt
    }
    amendments {
      __typename
      id
      submittedAt
    }
  }
`);

const getPreviousPatientChartsQuery = graphql(
  `
    query getPreviousPatientCharts($tenantId: uuid!, $patientId: uuid!) {
      appointments(
        where: {
          tenantId: { _eq: $tenantId }
          patientId: { _eq: $patientId }
          humanOutputs: { output: { content: { _isNull: false } } }
        }
        orderBy: { startDate: DESC }
        limit: 10
      ) {
        ...duplicatedAppointment
      }
    }
  `,
  [duplicateAppointmentFragment],
);

export interface NewAppointmentFormManualModeProps {
  isSettingsAvailable: boolean;
}

export const NewAppointmentFormManualMode: React.FC<
  NewAppointmentFormManualModeProps
> = ({ isSettingsAvailable }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const appointmentId = useAppointmentId();
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const { isDialogOpen: isOpen, toggleDialog: toggle } = useDialog();
  const [creatingAppointmentInProgress, setCreatingAppointmentInProgress] =
    useState(false);
  const isUserApptOwner = useIsUserAppointmentOwner();

  const [duplicateAppointment, setDuplicatedAppointment] = useState<ResultOf<
    typeof duplicateAppointmentFragment
  > | null>(null);

  const [settingsOpen, setSettingsOpen] = useState(isSettingsAvailable);
  const apiClient = useApiClient();

  const analytics = useAnalytics('manual_charting');

  const patient = useAppointmentPatient();

  // A template selection exists when an explicit bundle is stored OR the
  // default sentinel resolved to a computed default (ENG-4442). While the
  // default is still resolving, hasComputedDefault is false and the button
  // stays disabled — same UX as the selector's loading state.
  const { storedBundleId, hasComputedDefault } = useAppointmentBundleContext();
  const hasTemplateSelection = storedBundleId !== null || hasComputedDefault;

  const { data: previousChartData } = useQuery(getPreviousPatientChartsQuery, {
    variables: {
      tenantId,
      patientId: patient?.id ?? '',
    },
    skip: !patient?.id,
  });

  const previousCharts = previousChartData?.appointments ?? [];

  const startSession = async () => {
    analytics.capture('started_manual_charting_session', {
      appointmentId,
    });
    setCreatingAppointmentInProgress(true);

    const result =
      await apiClient.appointments.startAppointmentInManualChartingMode({
        params: {
          appointmentId,
          tenantId: tenantId!,
        },
        body: {
          duplicatedAppointmentId: duplicateAppointment?.id ?? '',
          userLocale: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });

    if (result.status !== 200) {
      toast.error(
        t('appointmentPage.newAppointment.manualChartingStartFailed'),
      );
      setCreatingAppointmentInProgress(false);
    }
  };

  const handleDialogOpenChange = async (isOpen: boolean) => {
    if (isOpen) {
      analytics.capture('user_opened_patient_chart_list', {
        appointmentId,
        tenantId,
        userId,
      });
    }

    toggle();
  };

  return (
    <Stack gap={5}>
      <Stack align="stretch">
        <Collapsible open={settingsOpen} onOpenChange={setSettingsOpen}>
          {isSettingsAvailable && (
            <Stack
              className="mb-5"
              align="stretch"
              justify="between"
              gap={2}
              direction="horizontal"
            >
              <Text inline weight="semibold">
                {t('appointmentPage.newAppointment.noteSettings')}
              </Text>
              <CollapsibleTrigger asChild>
                <Button variant="outline" size="sm">
                  {settingsOpen
                    ? t('appointmentPage.newAppointment.viewLessOptions')
                    : t('appointmentPage.newAppointment.viewMoreOptions')}
                </Button>
              </CollapsibleTrigger>
            </Stack>
          )}

          <NewAppointmentTemplateSelector />

          <CollapsibleContent
            className='h-0 data-[state="closed"]:overflow-hidden data-[state="open"]:h-auto data-[state="open"]:pt-5'
            data-state={settingsOpen ? 'open' : 'closed'}
            forceMount
          >
            <TenantOrUserFeatureFlaggedComponent flag="MULTILINGUAL_SUPPORT">
              <AppointmentLanguageSelector outputLanguage />
            </TenantOrUserFeatureFlaggedComponent>
          </CollapsibleContent>
        </Collapsible>
      </Stack>

      {Boolean(previousCharts.length) && (
        <Stack gap={3}>
          <Stack justify="between" gap={2} direction="horizontal">
            <Text weight="semibold">
              {t('appointmentPage.newAppointment.duplicatePreviousChart')}
            </Text>
            <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
              <DialogTrigger asChild>
                <Text
                  color="tertiary"
                  weight="semibold"
                  className="cursor-pointer underline"
                >
                  {t('appointmentPage.newAppointment.selectChart')}
                </Text>
              </DialogTrigger>
              <DialogContent size="lg">
                <DialogHeader>
                  <DialogTitle>
                    {t('appointmentPage.newAppointment.duplicateChartTitle')}
                  </DialogTitle>
                  <Text>
                    {t(
                      'appointmentPage.newAppointment.duplicateChartDescription',
                    )}
                  </Text>
                </DialogHeader>
                <Stack gap={4}>
                  {previousCharts.map((c) => (
                    <Stack
                      key={c.id}
                      justify="between"
                      className="cursor-pointer"
                      onClick={() => {
                        analytics.capture('user_selected_existing_chart', {
                          appointmentId,
                          tenantId,
                          userId,
                          chartId: c.id,
                          listPosition: previousCharts.indexOf(c),
                        });
                        setDuplicatedAppointment(c);
                        toggle();
                      }}
                      gap={2}
                      direction="horizontal"
                    >
                      <DuplicateChartCard
                        name={c.name}
                        startDate={c.startDate}
                      />
                      <AppointmentStatusLabel
                        isSummarized
                        status={c.status}
                        isSigned={Boolean(c.doctor_interaction?.noteSignedAt)}
                        isAmending={
                          Boolean(c.amendments.length) &&
                          c.amendments.some((a) => !a.submittedAt)
                        }
                        isAmended={
                          Boolean(c.amendments.length) &&
                          c.amendments.every((a) => a.submittedAt)
                        }
                      />
                    </Stack>
                  ))}
                </Stack>
              </DialogContent>
            </Dialog>
          </Stack>
          {Boolean(duplicateAppointment) && (
            <Stack
              className="rounded-lg border border-border-primary p-3"
              gap={2}
              direction="horizontal"
            >
              <DuplicateChartCard
                name={duplicateAppointment!.name}
                startDate={duplicateAppointment!.startDate}
              />
              <Button
                variant="ghost"
                onClick={() => {
                  analytics.capture('user_cancelled_duplicate_chart', {
                    appointmentId,
                    tenantId,
                    userId,
                    chartId: duplicateAppointment!.id,
                  });
                  setDuplicatedAppointment(null);
                }}
              >
                <Icon name="X" size="auto" />
              </Button>
            </Stack>
          )}
        </Stack>
      )}

      <Button
        size="lg"
        onClick={startSession}
        disabled={
          // Duplicating a previous chart provides the template info itself,
          // so no bundle selection/default is required in that flow.
          (!hasTemplateSelection && !duplicateAppointment) ||
          creatingAppointmentInProgress ||
          !isUserApptOwner
        }
        fullWidth
      >
        {t('appointmentPage.newAppointment.startSession')}
      </Button>
    </Stack>
  );
};
